/**
 * packaging.js — Packaging AI prompt tasks.
 * First task is the shared exemplar; the rest follow its schema exactly (see _exemplar.js).
 */
import { PACKAGING_CONCEPT_TASK } from "./_exemplar.js";

export const PACKAGING_TASKS = [
  PACKAGING_CONCEPT_TASK,

  // ── Creative brief for a human designer / agency ───────────────────────────
  {
    id: "packaging-creative-brief",
    category: "packaging",
    title: "Create Packaging Creative Brief",
    description: "A complete, agency-ready packaging design brief — objectives, mandatories, hierarchy, regulatory panel needs, deliverables, timeline and budget — you can hand to a designer today.",
    outputType: "text",
    recommendedTools: ["chatgpt", "claude", "gemini"],
    estimatedTime: "3 min to fill · 1 min in ChatGPT",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine", required: true, brandKey: "productDescription", span: "full" },
      { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 18–35 interested in fitness", required: true, brandKey: "targetConsumer", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", required: true, brandKey: "positioning", span: "full" },
      { id: "claims", label: "Key claims", type: "tags", placeholder: "Zero sugar, Natural caffeine, No artificial colors", brandKey: "claims" },
      { id: "competitors", label: "Competitors", type: "tags", placeholder: "Celsius, Alani Nu, Ghost", brandKey: "competitors" },
      { id: "style", label: "Desired style", type: "tags", placeholder: "Premium, Modern, Minimal, Bold", brandKey: "visualStyle" },
      { id: "brandColors", label: "Existing brand colors (optional)", type: "tags", placeholder: "Leave blank if none exist yet", brandKey: "brandColors" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Looking like a sports supplement; excessive neon colors", brandKey: "avoid" },
      { id: "channels", label: "Sales channels", type: "tags", placeholder: "DTC, Amazon, Natural grocery", brandKey: "channels" },
      { id: "pricePoint", label: "Retail price point", type: "text", placeholder: "$2.99 per can", brandKey: "pricePoint" },
      { id: "projectType", label: "Project type", type: "select", options: ["New brand launch", "Redesign of existing packaging", "Line / flavor extension", "Premium tier or special edition"], default: "New brand launch" },
      { id: "format", label: "Package format", type: "select", options: ["12 oz sleek can", "16 oz sleek can", "12 oz standard can", "Glass bottle", "PET bottle", "Stand-up pouch", "Flow-wrap bar", "Folding carton", "Jar", "Tub", "Other (describe in product description)"], default: "12 oz sleek can" },
      { id: "skuCount", label: "Number of SKUs in scope", type: "select", options: ["1", "2", "3", "4", "6", "8+"], default: "3" },
      { id: "timeline", label: "Timeline", type: "select", options: ["4 weeks", "6–8 weeks", "3 months", "Flexible — recommend a realistic schedule"], default: "6–8 weeks" },
      { id: "budgetRange", label: "Design budget", type: "select", options: ["Under $5,000", "$5,000–$15,000", "$15,000–$40,000", "$40,000+", "Not sure — recommend a range"], default: "Not sure — recommend a range" },
    ],
    template: `Act as a senior CPG packaging strategist who has written design briefs for brands that launched at Whole Foods, Target and Sprouts and who knows exactly what a freelance designer or agency needs to do great work on the first round.

Write a complete packaging design brief for {{brandName}}, {{category|article}}. Project type: {{projectType}}. The brief will be handed directly to a human designer or agency, so write it in clean, professional document form — not as advice to me.

PRODUCT
{{productDescription}}
Format: {{format}}. SKUs in scope: {{skuCount}}.{{#if pricePoint}} Retail price point: {{pricePoint}}.{{/if}}

BRAND POSITIONING
{{positioning}}

TARGET CONSUMER
{{targetConsumer}}

{{#if claims}}
KEY CLAIMS TO COMMUNICATE
{{claims|list}}
{{/if}}

{{#if competitors}}
COMPETITIVE SET
{{competitors|join}}. The design must be instantly distinguishable from these on a crowded shelf.
{{/if}}

{{#if channels}}
WHERE IT WILL SELL
{{channels|join}}. Account for how the pack reads in each of these environments (shelf distance, cooler door, thumbnail on Amazon / DTC).
{{/if}}

VISUAL DIRECTION
{{style|list}}
{{#unless style}}
- Premium
- Modern
- Distinctive
{{/unless}}
{{#if brandColors}}
Existing brand colors that must be respected: {{brandColors|join}}.
{{/if}}

{{#if avoid}}
AVOID
{{avoid|list}}
- Generic category clichés
- Cluttered front panels
{{/if}}
{{#unless avoid}}
AVOID
- Generic category clichés
- Cluttered front panels
{{/unless}}

WRITE THE BRIEF WITH THESE NUMBERED SECTIONS
1. Project overview (3–4 sentences: who we are, what we are making, why now)
2. Objectives and success criteria (4–6 measurable statements, e.g. "identifiable from 6 feet away", "wins the 3-second shelf test against {{competitors|default:the competitive set}}")
3. Brand background and positioning (rewrite my positioning into designer-friendly language)
4. Target consumer profile (demographics, mindset, where they shop, what they currently buy)
5. Competitive landscape (a table: competitor | visual codes they own | opening for us)
6. Design mandatories (a checklist: logo, brand name, product descriptor, flavor / variant name, net quantity statement, key claims (max 3 on front), UPC, nutrition or supplement panel, ingredient list, allergen statement, company address, certifications, QR / website — mark each as front, back or side panel)
7. Front-of-pack hierarchy (what the eye must read 1st, 2nd, 3rd, 4th)
8. Flavor / SKU architecture for {{skuCount}} SKU(s) (what stays fixed, what changes)
9. Regulatory and technical requirements (panel sizes, minimum type sizes, dieline and bleed, print process, finish options — and an explicit note that the designer must work from the printer's dieline and that all claims and panels require regulatory review; do not invent regulations, flag what to verify)
10. Deliverables list (concept rounds, revision rounds, final files: print-ready PDF, native AI files, dielines per SKU, 3D mockups, digital-ready PNG renders for e-commerce)
11. Timeline for {{timeline}} (a phase-by-phase table with milestones and who approves)
12. Budget ({{budgetRange}} — structure it by phase and note what is typically in or out of scope)
13. Evaluation criteria (how we will judge the concepts, tied back to section 2)
14. Questions for the designer to ask us before starting (5–8)

Keep the total under 1,200 words. Use tables where listed. Finish with a one-paragraph "what great looks like" statement the designer can pin above their desk.`,
    tips: [
      "Paste the output into a Google Doc, fill the gaps it flags, and send it with your logo files and a photo of the competitive shelf.",
      "Follow-up: “Turn section 5 into a mood-board shot list I can build in Midjourney.”",
      "Then run Create Packaging Concept and attach the best concept to the brief as a starting direction.",
    ],
  },

  // ── Flavor / variant extension of an existing system ──────────────────────
  {
    id: "packaging-flavor-extension",
    category: "packaging",
    title: "Create Flavor Extension",
    description: "A flavor architecture that extends your existing packaging system to new flavors — color assignments with hex codes, fixed vs. flexible elements, shelf-block strategy and an image prompt per flavor.",
    outputType: "text",
    recommendedTools: ["chatgpt", "claude", "gemini"],
    estimatedTime: "3 min to fill · 1 min in ChatGPT",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine", brandKey: "productDescription", span: "full" },
      { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 18–35 interested in fitness", brandKey: "targetConsumer", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", brandKey: "positioning", span: "full" },
      { id: "existingFlavor", label: "Existing flavor(s)", type: "tags", placeholder: "Original, Citrus Burst", required: true, help: "The flavors already on shelf." },
      { id: "newFlavors", label: "New flavors to add", type: "tags", placeholder: "Mango Chili, Blueberry Mint, Cherry Lime", required: true },
      { id: "existingDesign", label: "Describe the current design", type: "textarea", placeholder: "Matte black can, white wordmark centered, one accent color band near the top, flavor name in small caps", required: true, span: "full", help: "Layout, colors, typography, illustrations, finish." },
      { id: "existingColors", label: "Colors used on existing flavors", type: "tags", placeholder: "Original = #FF5A1F, Citrus = #FFD43B" },
      { id: "brandColors", label: "Existing brand colors (optional)", type: "tags", placeholder: "#111111, #F4F1EA", brandKey: "brandColors" },
      { id: "style", label: "Desired style", type: "tags", placeholder: "Premium, Modern, Minimal", brandKey: "visualStyle" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Childish fruit illustrations; neon", brandKey: "avoid" },
      { id: "format", label: "Package format", type: "select", options: ["12 oz sleek can", "16 oz sleek can", "12 oz standard can", "Glass bottle", "PET bottle", "Stand-up pouch", "Flow-wrap bar", "Folding carton", "Jar", "Tub"], default: "12 oz sleek can" },
      { id: "differentiationMethod", label: "How flavors differ today", type: "select", options: ["Color-block panel changes", "Accent color only", "Ingredient illustration or photo", "Pattern or texture change", "Let AI recommend"], default: "Let AI recommend" },
      { id: "channels", label: "Sales channels", type: "tags", placeholder: "DTC, Amazon, Natural grocery", brandKey: "channels" },
    ],
    template: `Act as a senior packaging designer who builds flavor architecture systems for multi-SKU CPG lines and knows how a range must block together on shelf.

Extend the existing packaging system of {{brandName}}, {{category|article}}, to new flavors without breaking what already works.{{#if productDescription}} Product: {{productDescription}}.{{/if}}

CURRENT DESIGN SYSTEM
{{existingDesign}}
Format: {{format}}. Current differentiation method: {{differentiationMethod}}.
Existing flavor(s): {{existingFlavor|join}}.
{{#if existingColors}}
Colors already assigned: {{existingColors|join}}.
{{/if}}
{{#if brandColors}}
Core brand colors that never change: {{brandColors|join}}.
{{/if}}

NEW FLAVORS TO ADD
{{newFlavors|numbered}}

{{#if positioning}}
BRAND POSITIONING
{{positioning}}
{{/if}}

{{#if targetConsumer}}
TARGET CONSUMER
{{targetConsumer}}
{{/if}}

{{#if channels}}
CHANNELS
{{channels|join}}. The range must read as one family on a physical shelf and as distinct thumbnails online.
{{/if}}

STYLE
{{style|list}}
{{#unless style}}
- Premium
- Consistent with the existing design
{{/unless}}

{{#if avoid}}
AVOID
{{avoid|list}}
- Colors so close that two flavors are confused at arm's length
- Redesigning the logo or the core layout
{{/if}}
{{#unless avoid}}
AVOID
- Colors so close that two flavors are confused at arm's length
- Redesigning the logo or the core layout
{{/unless}}

PROVIDE
1. Fixed vs. flexible elements: a two-column list of what never changes across the range and what is allowed to change per flavor
2. Flavor architecture table covering EVERY flavor (existing and new): flavor | primary flavor color (hex) | secondary / accent (hex) | flavor cue (ingredient, illustration, pattern) | flavor-name treatment | notes on contrast against the core brand colors
3. Color-spacing check: confirm that no two flavors share a hue family; if the existing colors already crowd a region of the color wheel, propose the fix
4. Shelf-block strategy: the recommended left-to-right order of flavors on shelf and why (light to dark, warm to cool, hero flavor at eye level)
5. Flavor naming conventions: how flavor names should be written on pack (case, length, descriptor words) with each new flavor rewritten to match
6. Digital thumbnail check: how each flavor stays identifiable at 100 px on Amazon / DTC
7. Production notes: which elements can be a single shared print plate and which change per SKU; finish recommendations
8. One image-generation prompt PER NEW FLAVOR (ChatGPT image generation, Midjourney or Firefly) describing the {{format}} with the exact colors, the brand name "{{brandName}}" and the flavor name rendered legibly, on a clean studio background
9. One line-up image prompt showing all flavors side by side in the recommended shelf order

Finish with a short paragraph on which new flavor should be the lead launch SKU and why.`,
    tips: [
      "Upload a photo of your current pack to ChatGPT before pasting this prompt so it matches the real design.",
      "Paste each prompt from #8 into ChatGPT image generation with your existing pack photo attached and say “match this design exactly, change only the flavor.”",
    ],
  },

  // ── Product render (image) ────────────────────────────────────────────────
  {
    id: "packaging-product-render",
    category: "packaging",
    title: "Create Product Render",
    description: "A photorealistic studio render of your package — choose format, background, angle and lighting, and get an image-ready prompt for ChatGPT, Midjourney or Firefly.",
    outputType: "image",
    recommendedTools: ["chatgpt-images", "midjourney", "firefly"],
    estimatedTime: "1 min to fill · 1 min to generate",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine", brandKey: "productDescription", span: "full" },
      { id: "claims", label: "Key claims", type: "tags", placeholder: "Zero sugar, Natural caffeine", brandKey: "claims" },
      { id: "style", label: "Desired style", type: "tags", placeholder: "Premium, Modern, Minimal", brandKey: "visualStyle" },
      { id: "brandColors", label: "Existing brand colors (optional)", type: "tags", placeholder: "#111111, #F4F1EA, #FF5A1F", brandKey: "brandColors" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Neon colors; gaming imagery", brandKey: "avoid" },
      { id: "format", label: "Package format", type: "select", options: ["12 oz sleek can", "16 oz sleek can", "12 oz standard can", "Glass bottle", "PET bottle", "Stand-up pouch", "Flow-wrap bar", "Folding carton", "Jar", "Tub"], default: "12 oz sleek can" },
      { id: "background", label: "Background", type: "select", options: ["Pure white seamless", "Soft grey studio gradient", "Brand-color backdrop", "Matte black", "Natural stone surface", "Light wood surface"], default: "Soft grey studio gradient" },
      { id: "angle", label: "Camera angle", type: "select", options: ["Straight-on front view", "Three-quarter hero angle", "Low-angle hero", "Top-down flat lay", "Line-up of three packs"], default: "Three-quarter hero angle" },
      { id: "lighting", label: "Lighting", type: "select", options: ["Soft diffused studio", "Dramatic rim light", "Bright high-key", "Warm golden hour", "Cool editorial"], default: "Soft diffused studio" },
      { id: "finish", label: "Material finish", type: "select", options: ["Matte", "Gloss", "Soft-touch matte", "Metallic with foil accents"], default: "Matte" },
      { id: "includeText", label: "Text on pack", type: "select", options: ["Legible brand name and claims", "Blank pack — no text"], default: "Legible brand name and claims" },
      { id: "aspectRatio", label: "Aspect ratio", type: "select", options: ["1:1", "4:5", "3:2", "16:9"], default: "1:1" },
    ],
    template: `Act as a senior CPG product photographer and 3D packaging render artist. Generate one photorealistic product render.

SUBJECT
A single {{format}} of {{brandName}}, {{category|article}}{{#if productDescription}} — {{productDescription}}{{/if}}.
Finished, commercial-grade packaging: crisp label edges, accurate real-world proportions for a {{format}}, {{finish|lower}} finish with believable material reflections and micro-texture.
{{#if angle=Line-up of three packs}}
Show three identical packs in a tight row, the center pack slightly forward, all perfectly aligned.
{{/if}}

{{#if includeText=Legible brand name and claims}}
TEXT ON PACK
The brand name "{{brandName}}" is the largest element on the front panel, spelled exactly, perfectly legible.
{{#if claims}}
Small supporting text only: {{claims|join}}.
{{/if}}
No other words, no invented lettering.
{{/if}}
{{#if includeText=Blank pack — no text}}
TEXT ON PACK
No text at all — a clean blank pack showing only color blocking and graphic shapes so the design system reads without typography.
{{/if}}

COLOR AND STYLE
Design mood: {{style|join}}{{#unless style}}premium, modern, minimal{{/unless}}. Clean layout, generous negative space, confident typography.
{{#if brandColors}}
Pack palette: {{brandColors|join}} — these dominate the pack; the backdrop stays neutral so the pack owns the color.
{{/if}}
{{#unless brandColors}}
Pack palette: one confident primary color plus a single accent, appropriate for a premium {{category|lower}}.
{{/unless}}

SCENE
Background: {{background}}.
Camera angle: {{angle}}.
Lighting: {{lighting}}, soft contact shadow grounding the product, subtle surface reflection, gentle highlight along the pack edge.
{{#if background=Brand-color backdrop}}
Backdrop color pulled from the pack palette, slightly darker than the pack so it still separates.
{{/if}}

CAMERA
100mm macro lens, f/8, tack-sharp focus across the whole pack, studio product photography, 8k detail, commercial advertising quality, color-accurate, no lens distortion.

{{#if avoid}}
AVOID
{{avoid|list}}
- Warped, duplicated or misspelled text
- Extra floating objects, hands or stray products
- Busy backgrounds competing with the pack
{{/if}}
{{#unless avoid}}
AVOID
- Warped, duplicated or misspelled text
- Extra floating objects, hands or stray products
- Busy backgrounds competing with the pack
{{/unless}}

NEGATIVE PROMPT
blurry, low resolution, distorted label, misspelled text, cartoon, illustration, watermark, cluttered, oversaturated, extra limbs, deformed can

ASPECT RATIO
{{aspectRatio}}`,
    tips: [
      "Upload your product image or logo first, then paste this prompt — ChatGPT will keep your real design.",
      "If the text comes out wrong, regenerate with: “Keep everything, fix the spelling of the brand name to [your brand name].”",
      "Use Midjourney with --style raw for the most photographic result; use ChatGPT for anything where the text must be exact.",
    ],
  },

  // ── Shelf display (image) ─────────────────────────────────────────────────
  {
    id: "packaging-shelf-display",
    category: "packaging",
    title: "Create Shelf Display Mockup",
    description: "A realistic retail shelf-set image of your product with competitor packs beside it, in the store environment you choose — perfect for buyer decks and gut-checking shelf impact.",
    outputType: "image",
    recommendedTools: ["chatgpt-images", "midjourney", "firefly"],
    estimatedTime: "1 min to fill · 1 min to generate",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, matte black with orange wordmark", brandKey: "productDescription", span: "full" },
      { id: "competitors", label: "Competitors", type: "tags", placeholder: "Celsius, Alani Nu, Ghost", brandKey: "competitors" },
      { id: "claims", label: "Key claims", type: "tags", placeholder: "Zero sugar, Natural caffeine", brandKey: "claims" },
      { id: "style", label: "Desired style", type: "tags", placeholder: "Premium, Modern, Minimal", brandKey: "visualStyle" },
      { id: "brandColors", label: "Existing brand colors (optional)", type: "tags", placeholder: "#111111, #F4F1EA, #FF5A1F", brandKey: "brandColors" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Neon colors; gaming imagery", brandKey: "avoid" },
      { id: "pricePoint", label: "Retail price point", type: "text", placeholder: "$2.99", brandKey: "pricePoint" },
      { id: "retailer", label: "Retail environment", type: "select", options: ["Natural grocery (Whole Foods / Sprouts style)", "Convenience store cooler", "Mass retail aisle (Target / Walmart style)", "Club store pallet (Costco style)", "Specialty / boutique shop"], default: "Natural grocery (Whole Foods / Sprouts style)" },
      { id: "facings", label: "Number of facings", type: "select", options: ["2", "3", "4", "6"], default: "3" },
      { id: "shelfPosition", label: "Shelf position", type: "select", options: ["Eye level, center", "Eye level, left", "Second shelf from top", "Bottom shelf"], default: "Eye level, center" },
      { id: "priceTag", label: "Shelf price tag", type: "select", options: ["Show a shelf price tag", "No price tag"], default: "Show a shelf price tag" },
      { id: "aspectRatio", label: "Aspect ratio", type: "select", options: ["16:9", "3:2", "4:5", "1:1"], default: "16:9" },
    ],
    template: `Act as a senior retail environment photographer who shoots planogram and shelf-set imagery for CPG brand presentations. Generate one photorealistic in-store shelf photograph.

HERO PRODUCT
{{facings}} facings of {{brandName}}, {{category|article}}{{#if productDescription}} — {{productDescription}}{{/if}}, positioned at {{shelfPosition|lower}}, fronts perfectly faced and aligned.
The brand name "{{brandName}}" is clearly legible on every facing, spelled exactly.
{{#if claims}}
Small claim text visible on the front panel: {{claims|join}}.
{{/if}}
{{#if brandColors}}
Pack colors: {{brandColors|join}}.
{{/if}}
Pack design mood: {{style|join}}{{#unless style}}premium, modern, minimal{{/unless}} — it should visibly stand out from its neighbors.

COMPETITOR CONTEXT
Shelves above and below fully stocked with blurred, generic {{category|lower}} products.
{{#if competitors}}
Directly to the left and right of the hero: generic, unbranded packs in the visual style of {{competitors|join}} (similar colors, shapes and layouts, but no real logos or trademarks), so the hero product reads against its real competitive set.
{{/if}}
{{#unless competitors}}
Directly to the left and right of the hero: generic, unbranded {{category|lower}} packs in typical category colors, so the hero product reads against realistic neighbors.
{{/unless}}

STORE ENVIRONMENT
{{retailer}}.
{{#if retailer=Natural grocery (Whole Foods / Sprouts style)}}
Warm wood-trimmed shelving, chalkboard-style signage hints, soft warm overhead lighting, clean and premium.
{{/if}}
{{#if retailer=Convenience store cooler}}
Glass-door reach-in cooler, bright cool LED strip lighting, light condensation on the glass, packs on wire shelves.
{{/if}}
{{#if retailer=Mass retail aisle (Target / Walmart style)}}
Long white metal gondola shelving, bright even fluorescent lighting, wide aisle, price rail along each shelf edge.
{{/if}}
{{#if retailer=Club store pallet (Costco style)}}
Warehouse setting with steel racking, product stacked on a pallet in open shipper trays, high ceilings, industrial lighting.
{{/if}}
{{#if retailer=Specialty / boutique shop}}
Curated open shelving, minimal product density, directional spot lighting, concrete or plaster walls.
{{/if}}
{{#if priceTag=Show a shelf price tag}}
A small realistic shelf-edge price tag under the hero product{{#if pricePoint}} reading {{pricePoint}}{{/if}}.
{{/if}}

CAMERA
Shot at shopper eye level from about 4 feet away, 35mm lens, f/4, hero product in sharp focus, neighbors slightly softer, natural perspective, realistic store photography, no HDR look.

{{#if avoid}}
AVOID
{{avoid|list}}
- Real competitor logos or trademarks
- Misspelled or warped text on the hero pack
- Empty shelves, floating products, people blocking the shelf
{{/if}}
{{#unless avoid}}
AVOID
- Real competitor logos or trademarks
- Misspelled or warped text on the hero pack
- Empty shelves, floating products, people blocking the shelf
{{/unless}}

NEGATIVE PROMPT
blurry, misspelled text, distorted packaging, duplicate logos, cartoon, illustration, watermark, empty shelf, fisheye distortion

ASPECT RATIO
{{aspectRatio}}`,
    tips: [
      "Upload your product image or a render from Create Product Render first so the hero pack matches your real design.",
      "Generate one version per retail environment you are pitching — buyers respond to seeing the product in their own store.",
      "Then ask: “Now show the same shelf from 10 feet away — can you still find my product?”",
    ],
  },

  // ── Multipack / variety pack ──────────────────────────────────────────────
  {
    id: "packaging-multipack",
    category: "packaging",
    title: "Create Multipack / Variety Pack",
    description: "A multipack design plan — structure recommendation, panel-by-panel hierarchy, club and mass-channel requirements, regulatory flags and a ready-to-use image prompt.",
    outputType: "text",
    recommendedTools: ["chatgpt", "claude", "gemini"],
    estimatedTime: "2 min to fill · 1 min in ChatGPT",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine", required: true, brandKey: "productDescription", span: "full" },
      { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 18–35 interested in fitness", brandKey: "targetConsumer", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", brandKey: "positioning", span: "full" },
      { id: "claims", label: "Key claims", type: "tags", placeholder: "Zero sugar, Natural caffeine", brandKey: "claims" },
      { id: "competitors", label: "Competitors", type: "tags", placeholder: "Celsius, Alani Nu, Ghost", brandKey: "competitors" },
      { id: "style", label: "Desired style", type: "tags", placeholder: "Premium, Modern, Minimal", brandKey: "visualStyle" },
      { id: "brandColors", label: "Existing brand colors (optional)", type: "tags", placeholder: "#111111, #F4F1EA, #FF5A1F", brandKey: "brandColors" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Neon colors; gaming imagery", brandKey: "avoid" },
      { id: "pricePoint", label: "Single-unit retail price", type: "text", placeholder: "$2.99 per can", brandKey: "pricePoint" },
      { id: "packType", label: "Pack type", type: "select", options: ["Single flavor multipack", "Variety pack — mixed flavors"], default: "Variety pack — mixed flavors" },
      { id: "packCount", label: "Units per pack", type: "select", options: ["4", "6", "8", "12", "15", "18", "24", "30"], default: "12" },
      { id: "flavors", label: "Flavors included", type: "tags", placeholder: "Original, Mango Chili, Blueberry Mint", help: "Leave blank for a single-flavor pack." },
      { id: "packChannel", label: "Primary channel for this pack", type: "select", options: ["Club (Costco / Sam's Club)", "Mass (Target / Walmart)", "Grocery", "Amazon / e-commerce (ships in own container)", "DTC subscription box"], default: "Club (Costco / Sam's Club)" },
      { id: "packStructure", label: "Pack structure", type: "select", options: ["Let AI recommend", "Printed paperboard carton", "Paperboard tray with shrink film", "Corrugated display tray (PDQ)", "Kraft wrap sleeve", "Printed corrugated shipper"], default: "Let AI recommend" },
    ],
    template: `Act as a senior CPG packaging designer and structural packaging engineer who has developed club, mass and e-commerce multipacks for beverage and snack brands.

Design a {{packCount}}-count {{packType|lower}} for {{brandName}}, {{category|article}}. Primary channel: {{packChannel}}. Preferred structure: {{packStructure}}.

SINGLE UNIT
{{productDescription}}
{{#if pricePoint}}
Single-unit retail price: {{pricePoint}}.
{{/if}}

{{#if flavors}}
FLAVORS IN THE PACK
{{flavors|list}}
{{/if}}

{{#if positioning}}
BRAND POSITIONING
{{positioning}}
{{/if}}

{{#if targetConsumer}}
TARGET CONSUMER
{{targetConsumer}}
{{/if}}

{{#if claims}}
KEY CLAIMS
{{claims|list}}
{{/if}}

{{#if competitors}}
COMPETITIVE SET
{{competitors|join}}. Note how their multipacks are structured and where ours can look more premium or more shoppable.
{{/if}}

VISUAL SYSTEM
Style: {{style|join}}{{#unless style}}premium, modern, consistent with the single unit{{/unless}}.
{{#if brandColors}}
Brand colors: {{brandColors|join}}.
{{/if}}

{{#if avoid}}
AVOID
{{avoid|list}}
- A multipack that hides the single-unit design
- Tiny flavor callouts that cannot be read from the aisle
{{/if}}
{{#unless avoid}}
AVOID
- A multipack that hides the single-unit design
- Tiny flavor callouts that cannot be read from the aisle
{{/unless}}

PROVIDE
1. Structure recommendation: the best structure for {{packChannel}} at {{packCount}} units, with a second option, trade-offs (cost, shelf presence, sustainability, line-speed) and whether a product window or die-cut is advised
2. Panel-by-panel hierarchy: a table with rows Front / Side A / Side B / Top / Back / Bottom and columns: must-have elements | hierarchy order | notes
3. Count and flavor communication: how "{{packCount}} x {{productDescription}}" and the flavor mix are called out so a shopper understands the pack in 3 seconds
4. Channel requirements for {{packChannel}}: pallet-ready and 360-degree visibility for club; price-per-unit and shelf-ready cues for mass and grocery; ships-in-own-container durability, frustration-free opening and a photography-friendly top panel for e-commerce; unboxing moment for DTC
5. Shopper math: how to express value (per-unit price, "save X%" panel) — flag that any savings claim must be verified against actual pricing
6. Regulatory flags: net quantity statement for the whole pack, "not labeled for individual sale" language if units lack full panels, nutrition and ingredient panel placement, UPC for the multipack vs. the unit — flag each for regulatory review and do not invent requirements
7. Production and dieline notes: board grade or film, print process, finish, what to ask the structural packaging supplier
8. Sustainability story: material choices and on-pack recycling guidance worth considering
9. A detailed image-generation prompt (ChatGPT image generation, Midjourney or Firefly) showing the finished {{packCount}}-pack on a clean studio background, brand name "{{brandName}}" legible, with {{#if flavors}}the flavor mix{{/if}}{{#unless flavors}}the single flavor{{/unless}} visible

Finish with a one-paragraph recommendation on which structure to prototype first and the one question to settle with the co-packer before design starts.`,
    tips: [
      "Paste #9 into ChatGPT image generation with your single-unit photo attached so the multipack uses your real design.",
      "Follow-up: “Give me the Costco version and the Amazon version side by side — what changes between them?”",
    ],
  },

  // ── Evaluate existing packaging ───────────────────────────────────────────
  {
    id: "packaging-evaluate",
    category: "packaging",
    title: "Evaluate My Packaging",
    description: "A buyer's-eye audit of your current packaging — 3-second shelf test, 10-criterion scored rubric, regulatory red flags and the five fixes that matter most.",
    outputType: "text",
    recommendedTools: ["chatgpt", "claude", "gemini"],
    estimatedTime: "3 min to fill · 1 min in ChatGPT",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "pasteContent", label: "Describe your packaging (or note that you uploaded a photo)", type: "textarea", placeholder: "I have uploaded a photo of the front and back. / Matte black 12 oz can, white wordmark top third, orange stripe, claims in 8 pt under the logo...", required: true, span: "full", help: "Be specific: colors, layout, type sizes, claims, finish, what is on the back." },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine", brandKey: "productDescription", span: "full" },
      { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 18–35 interested in fitness", brandKey: "targetConsumer", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", brandKey: "positioning", span: "full" },
      { id: "claims", label: "Key claims", type: "tags", placeholder: "Zero sugar, Natural caffeine", brandKey: "claims" },
      { id: "competitors", label: "Competitors", type: "tags", placeholder: "Celsius, Alani Nu, Ghost", brandKey: "competitors" },
      { id: "pricePoint", label: "Retail price point", type: "text", placeholder: "$2.99 per can", brandKey: "pricePoint" },
      { id: "channels", label: "Sales channels", type: "tags", placeholder: "DTC, Amazon, Natural grocery", brandKey: "channels" },
      { id: "retailer", label: "Shelf context to judge against", type: "select", options: ["Natural grocery (Whole Foods / Sprouts)", "Conventional grocery", "Convenience store", "Mass retail (Target / Walmart)", "Club store", "Amazon / online thumbnail"], default: "Natural grocery (Whole Foods / Sprouts)" },
      { id: "evaluationFocus", label: "Why are you evaluating?", type: "select", options: ["General shelf-impact audit", "Deciding whether to redesign", "Preparing for a retailer pitch", "Comparing against competitors"], default: "General shelf-impact audit" },
    ],
    template: `Act as a senior category buyer, formerly at Whole Foods, who now audits packaging for emerging brands — direct, specific and focused on what actually moves product off the shelf. Do not flatter; score honestly.

Evaluate the current packaging of {{brandName}}, {{category|article}}, as it would perform in {{retailer}}. Purpose of this audit: {{evaluationFocus}}.

THE PACKAGING
{{pasteContent}}
If a photo is attached, evaluate the photo as the primary source and use the description for context.

{{#if productDescription}}
PRODUCT
{{productDescription}}
{{#if pricePoint}}
Retail price: {{pricePoint}}.
{{/if}}
{{/if}}

{{#if positioning}}
INTENDED POSITIONING
{{positioning}}
Judge whether the pack actually communicates this.
{{/if}}

{{#if targetConsumer}}
TARGET CONSUMER
{{targetConsumer}}
{{/if}}

{{#if claims}}
CLAIMS THE BRAND WANTS TO OWN
{{claims|list}}
{{/if}}

{{#if competitors}}
COMPETITIVE SET
{{competitors|join}}. Compare against what these brands do on the same shelf.
{{/if}}

{{#if channels}}
CHANNELS
{{channels|join}}.
{{/if}}

PROVIDE
1. The 3-second shelf test: what a shopper walking past would register 1st, 2nd and 3rd — and what they would miss entirely. State plainly whether the brand name, category and key benefit are all caught within 3 seconds.
2. Scored rubric — a table with columns: criterion | score (1–10) | evidence from the pack | one fix. Score each of these 10 criteria:
   1) Brand identification at 6 feet
   2) Category clarity (is it obvious what this is?)
   3) Differentiation from the competitive set
   4) Front-panel hierarchy and focal point
   5) Legibility at arm's length (type size, contrast)
   6) Color blocking and shelf presence
   7) Claims communication (right claims, right order, not too many)
   8) Premium cue vs. price point (does it look worth {{pricePoint|default:its price}}?)
   9) Flavor / variant navigation
   10) Digital thumbnail performance (how it reads at 100 px)
   Then give the total out of 100 and a one-line verdict.
3. Regulatory and compliance red flags: net quantity placement and size, claims that may need substantiation or specific wording, allergen and ingredient declarations, nutrition or supplement panel presence, required statements. Flag each as "verify with regulatory counsel" — do not state rules as fact or invent requirements.
4. What to keep: the 2–3 elements that are working and must survive any redesign.
5. Top 5 fixes, ranked by shelf impact vs. effort, each with a concrete instruction a designer could act on tomorrow.
6. Redesign or refine? A clear recommendation: leave it, refine (small changes), or redesign — with reasoning tied to {{evaluationFocus|lower}}.
7. Buyer verdict: one paragraph written as if you were deciding whether to give this product 3 facings in {{retailer}}.

AVOID
- Generic praise or generic advice; every point must reference something specific on this pack
- Inventing regulatory rules, type-size requirements or competitor facts`,
    tips: [
      "Upload clear photos of the front, back and side panels to ChatGPT before pasting — it will evaluate the real thing.",
      "Follow-up: “Rewrite the front panel hierarchy as a numbered list a designer can implement, keeping my logo unchanged.”",
      "Then paste the top 5 fixes into Create Packaging Creative Brief as the project objectives.",
    ],
  },

  // ── Label & back-panel copy ───────────────────────────────────────────────
  {
    id: "packaging-label-copy",
    category: "packaging",
    title: "Write Label & Back-Panel Copy",
    description: "Complete on-pack copy — front-panel hierarchy, romance copy in three lengths, a claims table, usage instructions and a regulatory checklist of everything to verify before print.",
    outputType: "text",
    recommendedTools: ["claude", "chatgpt", "gemini"],
    estimatedTime: "3 min to fill · 1 min in Claude",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine", required: true, brandKey: "productDescription", span: "full" },
      { id: "flavorName", label: "Flavor / variant name", type: "text", placeholder: "Mango Chili" },
      { id: "netWeight", label: "Net weight / size", type: "text", placeholder: "12 FL OZ (355 mL)", required: true },
      { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 18–35 interested in fitness", brandKey: "targetConsumer", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", brandKey: "positioning", span: "full" },
      { id: "claims", label: "Key claims", type: "tags", placeholder: "Zero sugar, Natural caffeine, No artificial colors", brandKey: "claims" },
      { id: "ingredients", label: "Hero ingredients", type: "tags", placeholder: "Green coffee, L-theanine", brandKey: "ingredients" },
      { id: "brandVoice", label: "Brand voice", type: "textarea", placeholder: "Direct and encouraging. Short sentences. No hype words.", brandKey: "brandVoice", span: "full" },
      { id: "personality", label: "Brand personality", type: "tags", placeholder: "Confident, Clean, Energetic", brandKey: "personality" },
      { id: "founderStory", label: "Founder story (optional)", type: "textarea", placeholder: "Started by two trainers who were tired of...", brandKey: "founderStory", span: "full" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Hype words; medical-sounding promises", brandKey: "avoid" },
      { id: "usage", label: "How to use / serving suggestion", type: "text", placeholder: "Serve chilled. One can per day." },
      { id: "requiredStatements", label: "Required statements you already know about", type: "textarea", placeholder: "Contains caffeine warning; Certified organic; Made in USA; allergen statement", span: "full", help: "Certifications, warnings, country of origin, etc." },
      { id: "format", label: "Package format", type: "select", options: ["12 oz sleek can", "16 oz sleek can", "12 oz standard can", "Glass bottle", "PET bottle", "Stand-up pouch", "Flow-wrap bar", "Folding carton", "Jar", "Tub"], default: "12 oz sleek can" },
    ],
    template: `Act as a senior CPG packaging copywriter who has written labels for natural food and beverage brands and works closely with regulatory consultants — you write copy that sells and you know which words trigger compliance review.

Write the complete on-pack copy for {{brandName}}{{#if flavorName}} {{flavorName}}{{/if}}, {{category|article}}, in {{format|article}}.

PRODUCT
{{productDescription}}
Net quantity: {{netWeight}}.{{#if usage}} Usage: {{usage}}.{{/if}}

{{#if positioning}}
BRAND POSITIONING
{{positioning}}
{{/if}}

{{#if targetConsumer}}
TARGET CONSUMER
{{targetConsumer}}
{{/if}}

{{#if claims}}
CLAIMS TO WORK WITH
{{claims|list}}
{{/if}}

{{#if ingredients}}
HERO INGREDIENTS
{{ingredients|list}}
{{/if}}

VOICE
{{#if brandVoice}}
{{brandVoice}}
{{/if}}
{{#if personality}}
Personality: {{personality|join}}.
{{/if}}
{{#unless brandVoice}}
{{#unless personality}}
Confident, clear and human. Short sentences. No hype.
{{/unless}}
{{/unless}}

{{#if founderStory}}
FOUNDER STORY (for an optional back-panel note)
{{founderStory}}
{{/if}}

{{#if requiredStatements}}
REQUIRED STATEMENTS ALREADY KNOWN
{{requiredStatements|list}}
{{/if}}

{{#if avoid}}
AVOID
{{avoid|list}}
- Disease, treatment or cure language of any kind
- Superlatives we cannot prove ("#1", "best", "healthiest")
{{/if}}
{{#unless avoid}}
AVOID
- Disease, treatment or cure language of any kind
- Superlatives we cannot prove ("#1", "best", "healthiest")
{{/unless}}

PROVIDE
1. Front panel (principal display panel) copy in hierarchy order: brand name, product descriptor / statement of identity, flavor name, up to 3 front-of-pack claims, net quantity statement exactly as "{{netWeight}}" — note where each sits
2. Back-panel romance copy in three lengths: 40 words, 80 words and 150 words — same message, same voice, the 40-word version must work on its own
3. Claims hierarchy table: claim | claim type (nutrient content, structure/function, ingredient, marketing, certification) | suggested on-pack wording | placement | regulatory note ("verify substantiation", "may require disclaimer", "check certifier usage rules")
4. Usage / serving copy: 1–2 lines, plus a "best enjoyed" suggestion that fits the target consumer
5. Ingredient storytelling: one line per hero ingredient explaining why it is there, in plain language, no health outcome promises
6. Optional founder note: 35–50 words, first person, signed{{#unless founderStory}} (skip if no founder story is provided){{/unless}}
7. Side-panel and utility copy: website / social handle line, recycling or how-to-dispose line, a QR-code call to action
8. Regulatory checklist: a table of every element that must be verified by a regulatory or legal reviewer before print — statement of identity, net quantity format and placement, ingredient list order, allergen declaration, nutrition or supplement facts panel, caffeine or other warnings, certification logo usage, country of origin, manufacturer address. Mark each "verify" — do not invent rules or state requirements as fact
9. Three alternative front-panel descriptor lines, so we can test which one reads fastest

Write every line ready to paste into the artwork file. Keep all copy within the voice above.`,
    tips: [
      "Paste the result into Claude and ask: “Cut every line by 20% without losing the meaning” — pack real estate is always smaller than you think.",
      "Send the claims table and regulatory checklist to a food-labeling consultant before your designer places any text.",
    ],
  },
];
